<template>
  <div class="pablotify-wrapper">

    <button v-if="showButton" class="plan-it-btn" @click="openModal">
      Comm Center
    </button>


    <!-- Pablo-style popup modal -->
    <div v-if="popupOpen" class="popup-overlay" @click.self="closePopup">
      <div class="popup-content">

        <!-- Header -->
        <div class="popup-header">
          <div class="modal-title-wrap">
            <div class="modal-title-line">
              <h2 class="page-heading" style="margin:0; font-size:24px;">Comm Center</h2>
            </div>
            <div class="modal-subtitle">
              {{ dept }} · Location {{ loc }}
            </div>
          </div>

          <button class="close-btn" @click="closePopup">✕</button>
        </div>

        

        <!-- INBOX -->
        <div v-if="activeTab === 'inbox'" class="panel" style="margin-top:12px;">
          <div class="toolbar" style="display:flex; align-items:center; gap:12px;">
            <button class="btn btn-small" @click="loadInbox">Refresh</button>

            <label style="display:flex; align-items:center; gap:8px; cursor:pointer;">
              <input type="checkbox" v-model="includeClosed" @change="loadInbox" />
              <span class="small-muted">Show Closed</span>
            </label>
          </div>


          <div v-if="loadingInbox" class="small-muted">Loading inbox…</div>

          <div v-else-if="inbox.length === 0" class="empty-unplanned">
            No requests yet.
          </div>

          <div v-else class="inbox-list">
            <div
              v-for="row in inbox"
              :key="row.request_id"
              class="inbox-item"
              :class="{ unread: row.is_read === false && row.recipient_dept === dept }"
              @click="onInboxRowClick(row)"
            >
              <div class="inbox-top">
                <div class="inbox-type">{{ prettyType(row.request_type) }}</div>
                <div class="small-muted">{{ formatDateTime(row.request_created_at) }}</div>
              </div>

              <div class="inbox-mid">
                <span class="pill">{{ row.sender_dept }} → {{ row.recipient_dept }}</span>
                <span v-if="row.item_no" class="pill">Item: {{ row.item_no }}</span>
                <span v-if="row.requires_replan" class="pill danger-pill">Replan</span>

                <span v-if="row.is_closed" class="pill">Resolved</span>
              </div>

              <div class="inbox-body">
                {{ renderPreview(row.request_type, row.payload) }}
              </div>

              <div v-if="row.response_type" class="small-muted" style="margin-top:6px;">
                ✅ {{ renderResponse(row) }}
              </div>


              <!-- ✅ Show Production Response -->
              <div v-if="row.response_type" class="response-box">
                <div class="response-title">
                  ✅ Production Response:
                  <strong>{{ prettyResponse(row.response_type, row.response_payload) }}</strong>
                </div>

                <div v-if="row.responded_at" class="small-muted" style="margin-top:4px;">
                  {{ formatDateTime(row.responded_at) }}
                </div>
              </div>

              <!-- ✅ Logistics-only action: Move out order if Production says NO -->
              <div
                v-if="dept === 'Logistics' && row.response_type && row.response_type.startsWith('NO_')"
                class="controls-wrap"
                style="justify-content:flex-start; margin-top:10px;"
              >
                <button class="btn btn-small" @click.stop="openMoveOut(row)">
                  Move Out Order
                </button>
              </div>



              <!-- ✅ Production-only Response Controls -->
                <div
                v-if="getResponseOptionsForRow(row).length > 0 && !row.response_type"
                class="panel"
                style="margin-top:10px; border-color:#333;"
                @click.stop
                >

                <div class="controls-wrap" style="justify-content:flex-start;">

                    <div class="control" style="max-width:260px;">
                    <label>Response</label>
                    <select v-model="draftResponses[row.request_id].response_type">
                        <option disabled value="">Select…</option>
                        <option
                          v-for="opt in getResponseOptionsForRow(row)"
                          :key="opt.code"
                          :value="opt.code"
                        >
                          {{ opt.label }}
                        </option>

                    </select>
                    </div>

                    <!-- ✅ Expected Date only if YES_EXPECTED_DATE -->
                    <div
                    v-if="draftResponses[row.request_id].response_type === 'YES_EXPECTED_DATE'"
                    class="control"
                    style="max-width:200px;"
                    >
                    <label>Expected Date</label>
                    <input type="date" v-model="draftResponses[row.request_id].expected_date" />
                    </div>

                    <div class="control" style="max-width:150px;">
                    <label>&nbsp;</label>
                    <button
                        class="btn btn-small"
                        @click.stop="sendResponse(row)"
                        :disabled="responding && responding[row.request_id]"
                    >
                        {{ responding[row.request_id] ? "Sending…" : "Send" }}
                    </button>
                    </div>
                </div>

                <div v-if="responseErrors[row.request_id]" class="small-muted" style="color:#ff5c5c;">
                    {{ responseErrors[row.request_id] }}
                </div>
                </div>


            </div>
          </div>
        </div>

        <!-- NEW REQUEST -->
        <div v-if="activeTab === 'new'" class="panel" style="margin-top:12px;">
          <div class="controls-wrap" style="justify-content:flex-start;">

            <div class="control" style="max-width:240px;">
              <label>Recipient Dept</label>
              <select v-model="newRequest.recipient_dept">
                <option disabled value="">Select…</option>
                <option v-for="d in deptOptions" :key="d" :value="d">
                  {{ d }}
                </option>
              </select>
            </div>

            <div class="control" style="max-width:280px;">
              <label>Request Type</label>
              <select v-model="newRequest.request_type">
                <option disabled value="">Select…</option>
                <option v-for="t in requestTypes" :key="t.code" :value="t.code">
                  {{ t.label }}
                </option>
              </select>
            </div>
          </div>

          <!-- Dynamic Fields -->
          <div v-if="newRequest.request_type" class="panel" style="margin-top:10px; border-color:#333;">
            <div class="controls-wrap" style="justify-content:flex-start;">

              <!-- READY_BY_DATE -->
              <template v-if="newRequest.request_type === 'READY_BY_DATE'">
                <div class="control" style="max-width:200px;">
                  <label>Item No</label>
                  <input v-model="newRequest.payload.item_no" placeholder="12345" />
                </div>

                <div class="control" style="max-width:200px;">
                  <label>Req Ship Date</label>
                  <input type="date" v-model="newRequest.payload.req_ship_dt" />
                </div>

                <div class="control" style="max-width:320px;">
                    <label>Order No(s)</label>

                    <select multiple v-model="newRequest.payload.order_nos" style="min-height:120px;">
                        <option v-for="o in orderOptions" :key="o" :value="o">
                        {{ o }}
                        </option>
                    </select>

                    <div v-if="loadingOrders" class="small-muted">Loading orders…</div>
                    <div v-else class="small-muted">
                        Hold Ctrl (Windows) / Cmd (Mac) to select multiple
                   </div>
                </div>

              </template>

              <!-- MOVE_OUT_ORDERS -->
              <template v-if="newRequest.request_type === 'MOVE_OUT_ORDERS'">
                <div class="control" style="max-width:320px;">
                    <label>Order No(s)</label>

                    <select multiple v-model="newRequest.payload.order_nos" style="min-height:120px;">
                        <option v-for="o in orderOptions" :key="o" :value="o">
                        {{ o }}
                        </option>
                    </select>

                    <div v-if="loadingOrders" class="small-muted">Loading orders…</div>
                    <div v-else class="small-muted">
                        Hold Ctrl (Windows) / Cmd (Mac) to select multiple
                  </div>
                </div>


                <div class="control" style="max-width:200px;">
                  <label>Original Ship Date</label>
                  <input type="date" v-model="newRequest.payload.original_ship_dt" />
                </div>

                <div class="control" style="max-width:200px;">
                  <label>New Ship Date</label>
                  <input type="date" v-model="newRequest.payload.new_ship_dt" />
                </div>
              </template>

              <!-- ORDERS_MOVED_RERUN -->
              <template v-if="newRequest.request_type === 'ORDERS_MOVED_RERUN'">
                <div class="control" style="max-width:320px;">
                    <label>Order No(s)</label>

                    <select multiple v-model="newRequest.payload.order_nos" style="min-height:120px;">
                        <option v-for="o in orderOptions" :key="o" :value="o">
                        {{ o }}
                        </option>
                    </select>

                    <div v-if="loadingOrders" class="small-muted">Loading orders…</div>
                    <div v-else class="small-muted">
                        Hold Ctrl (Windows) / Cmd (Mac) to select multiple
                   </div>
                </div>


                <div class="control" style="max-width:200px;">
                  <label>Original Ship Date</label>
                  <input type="date" v-model="newRequest.payload.original_ship_dt" />
                </div>

                <div class="control" style="max-width:200px;">
                  <label>New Ship Date</label>
                  <input type="date" v-model="newRequest.payload.new_ship_dt" />
                </div>

                <div class="control" style="max-width:220px;">
                  <label>Requires Replan?</label>
                  <select v-model="newRequest.requires_replan">
                    <option :value="false">No</option>
                    <option :value="true">Yes</option>
                  </select>
                </div>
              </template>

            </div>
          </div>

          <!-- Preview -->
          <div class="panel preview-panel" style="margin-top:12px; border-style:dashed; border-color:#666;">
            <div style="font-weight:bold; margin-bottom:6px;">Preview</div>
            <div>{{ renderPreview(newRequest.request_type, newRequest.payload, true) }}</div>
          </div>

          <!-- Actions -->
          <div style="margin-top:12px; display:flex; justify-content:flex-end; gap:10px;">
            <button class="btn" @click="sendRequest" :disabled="sending">
              {{ sending ? 'Sending…' : 'Send Request' }}
            </button>
          </div>

          <div v-if="sendError" class="small-muted" style="color:#ff5c5c; margin-top:10px;">
            {{ sendError }}
          </div>

        </div>

      </div>
    </div>

  </div>

  <!-- ✅ THREAD DETAIL MODAL -->
<div
  v-if="showThreadModal"
  class="popup-overlay"
  @click.self="closeThread"
>
  <div class="popup-content" style="max-width: 820px;">
    <div class="popup-header">
      <div class="modal-title-wrap">
        <div class="modal-title-line">
          <h3 style="margin:0;">Thread #{{ threadDetail?.thread_id }}</h3>
        </div>
        <div class="modal-subtitle">
          Item: <strong>{{ threadDetail?.item_no || "(none)" }}</strong>
        </div>
      </div>
      <button class="close-btn" @click="closeThread">✕</button>
    </div>

    <!-- ✅ Thread Actions -->
      <div
        v-if="threadDetail && !threadDetail.is_closed"
        class="controls-wrap"
        style="justify-content:flex-start; margin-top:10px;"
      >
        <button class="btn btn-small" @click="closeThreadServer">
          Mark Resolved
        </button>
      </div>

      <div
        v-else-if="threadDetail && threadDetail.is_closed"
        class="small-muted"
        style="margin-top:10px;"
      >
        ✅ This thread is resolved.
      </div>


    <div v-if="loadingThread" class="small-muted" style="margin-top:12px;">
      Loading thread…
    </div>

    <div v-else style="text-align:left; margin-top:12px;">

      <div v-if="threadMessages.length === 0" class="small-muted">
        No messages in thread.
      </div>

      <div v-else class="thread-list">
        <div
          v-for="m in threadMessages"
          :key="m.request_id"
          class="thread-msg panel"
          style="border-color:#333; margin-bottom:10px;"
        >
          <div class="inbox-top">
            <div class="inbox-type">{{ prettyType(m.request_type) }}</div>
            <div class="small-muted">{{ formatDateTime(m.created_at) }}</div>
          </div>

          <div class="inbox-mid" style="margin-top:6px;">
            <span class="pill">{{ m.sender_dept }} → {{ m.recipient_dept }}</span>
            <span v-if="m.payload?.item_no" class="pill">Item: {{ m.payload.item_no }}</span>
          </div>

          <div class="inbox-body" style="margin-top:8px;">
            {{ renderPreview(m.request_type, m.payload) }}
          </div>

          <div v-if="m.response_type" class="response-box" style="margin-top:10px;">
            <div class="response-title">
              ✅ Response: <strong>{{ prettyResponse(m.response_type, m.response_payload) }}</strong>
            </div>
            <div v-if="m.responded_at" class="small-muted" style="margin-top:4px;">
              {{ formatDateTime(m.responded_at) }}
            </div>
          </div>
        </div>
      </div>

    </div>
  </div>
</div>


  <!-- ✅ MOVE OUT MODAL (Logistics) -->
<div
  v-if="showMoveOutModal"
  class="popup-overlay"
  @click.self="closeMoveOut"
>
  <div class="popup-content" style="max-width: 650px;">
    <div class="popup-header">
      <div class="modal-title-wrap">
        <div class="modal-title-line">
          <h3 style="margin:0;">Move Out Order(s)</h3>
        </div>
        <div class="modal-subtitle">
          Item: <strong>{{ moveOutForm.item_no }}</strong>
        </div>
      </div>

      <button class="close-btn" @click="closeMoveOut">✕</button>
    </div>

    <div style="text-align:left;">

      <!-- ✅ Orders (read-only list) -->
      <div class="control" style="margin-top:10px;">
        <label>Orders</label>
        <div class="panel" style="border-color:#333;">
          {{ (moveOutForm.order_nos || []).join(", ") || "None" }}
        </div>
      </div>

      <!-- ✅ Original Ship Date -->
      <div class="control" style="margin-top:10px;">
        <label>Original Ship Date</label>
        <div class="panel" style="border-color:#333;">
          {{ formatDateOnly(moveOutForm.original_ship_dt) || "(unknown)" }}
        </div>
      </div>

      <!-- ✅ New Ship Date -->
      <div class="control" style="margin-top:10px; max-width:220px;">
        <label>New Ship Date</label>
        <input type="date" v-model="moveOutForm.new_ship_dt" />
      </div>

      <div class="control" style="margin-top:10px;">
        <label style="display:flex; align-items:center; gap:8px;">
          <input type="checkbox" v-model="moveOutForm.confirm_locked" />
          Truck is locked (Macola updated)
        </label>
      </div>


      <!-- ✅ Reason (dropdown only) -->
      <div class="control" style="margin-top:10px; max-width:260px;">
        <label>Reason</label>
        <select v-model="moveOutForm.reason_code">
          <option disabled value="">Select…</option>
          <option value="RM_SHORTAGE">RM Shortage</option>
          <option value="MACHINE_DOWN">Machine Down</option>
          <option value="CAPACITY">Capacity Constraint</option>
          <option value="OTHER">Other</option>
        </select>
      </div>

      <!-- ✅ Submit -->
      <div class="controls-wrap" style="justify-content:flex-start; margin-top:18px;">
        <button
          class="btn btn-small"
          @click="submitMoveOut"
          :disabled="!moveOutForm.new_ship_dt || !moveOutForm.confirm_locked || sendingMoveOut"
        >
          {{ sendingMoveOut ? "Sending…" : "Send Move-Out Request" }}
        </button>
      </div>

      <div v-if="moveOutError" class="small-muted" style="margin-top:10px; color:#ff5c5c;">
        {{ moveOutError }}
      </div>

    </div>
  </div>
</div>

</template>

<script>

import axios from 'axios';


export default {
  name: "PablotifyModal",

  props: {
    dept: { type: String, required: true }, // "Logistics" or "Production"
    loc: { type: String, default: "D" },
    showButton: {
      type: Boolean,
      default: true
    }
  },

  data() {
    return {
      popupOpen: false,
      activeTab: "inbox",
      unreadCount: 0,
      inbox: [],
      loadingInbox: false,
      orderOptions: [],
      loadingOrders: false,
      includeClosed: false, 


      deptOptions: ["Logistics", "Production"],

      requestTypes: [
        { code: "READY_BY_DATE", label: "Will item be ready by date?" },
        { code: "MOVE_OUT_ORDERS", label: "Move out order(s)" },
        { code: "ORDERS_MOVED_RERUN", label: "Orders moved — rerun planner" }
      ],

      newRequest: {
        recipient_dept: "",
        request_type: "",
        payload: {},
        requires_replan: false
      },

      responseOptions: [
        { code: "YES_EXPECTED_DATE", label: "Yes — expected date" },
        { code: "NO_RM_SHORTAGE", label: "No — RM shortage" },
        { code: "NO_MACHINE_DOWN", label: "No — machine down" }
      ],

      productionResponseOptions: [
        { code: "YES_EXPECTED_DATE", label: "Yes – expected date" },
        { code: "NO_RM_SHORTAGE", label: "No – RM shortage" },
        { code: "NO_MACHINE_DOWN", label: "No – machine down" },
        { code: "NO_CAPACITY", label: "No – capacity" }
      ],

      logisticsResponseOptions: [
        { code: "WILL_MOVE_OUT", label: "Will move out" },
        { code: "MOVED_OUT", label: "Moved out" },
        { code: "MOVED_AND_LOCKED", label: "Moved + locked" },
        { code: "CANNOT_MOVE_OUT", label: "Cannot move out" }
      ],


      draftResponses: {},
      responding: {},
      responseErrors: {},

      showMoveOutModal: false,
      moveOutRow: null,
      moveOutError: "",
      sendingMoveOut: false,

      showThreadModal: false,
      threadDetail: null,
      threadMessages: [],
      loadingThread: false,


      moveOutForm: {
        new_ship_dt: "",
        original_ship_dt: "",
        order_nos: [],
        item_no: "",
        reason_code: "",
        confirm_locked: false   // ✅ NEW
      },

      
      sending: false,
      sendError: "",
      pollTimer: null
    };
  },

  watch: {
    orderNosText(val) {
      const arr = val
        .split(",")
        .map(s => s.trim())
        .filter(Boolean);
      this.newRequest.payload.order_nos = arr;
    },

    unreadCount(val) {
      this.$emit("unread-count", val);
    },

    'newRequest.request_type'() {
    if (!this.newRequest.payload) this.newRequest.payload = {};
    this.newRequest.payload.order_nos = [];
    }
  },

  mounted() {
    this.loadUnreadCount();
    if (this.pollTimer) clearInterval(this.pollTimer);
    this.pollTimer = setInterval(() => {
      this.loadUnreadCount();
    }, 30000);
  },


  beforeUnmount() {
    if (this.pollTimer) clearInterval(this.pollTimer);
  },



  methods: {
    openPopup() {
      this.popupOpen = true;
      this.activeTab = "inbox";
      this.loadInbox();
      this.loadOrderOptions();

    },

    closePopup() {
      this.popupOpen = false;
    },

    formatDateOnly(val) {
      if (!val) return '';
      try {
        return String(val).slice(0, 10); // "2025-12-30"
      } catch (e) {
        return val;
      }
    },

    openModal() {
      this.showModal = true;
      this.activeTab = "inbox";
      this.loadInbox();
    },

    openMoveOut(row) {
      const payload = row.payload || {};

      this.moveOutRow = row;
      this.moveOutForm = {
        new_ship_dt: "",
        original_ship_dt: payload.req_ship_dt || payload.orig_req_ship_dt || "",
        order_nos: payload.order_nos || [],
        item_no: payload.item_no || row.item_no || "",
        reason_code: row.response_type || ""
      };

      this.showMoveOutModal = true;
    },

    closeMoveOut() {
      this.showMoveOutModal = false;
      this.moveOutRow = null;
    },

    async openThread(row) {
      this.loadingThread = true;
      this.showThreadModal = true;
      this.threadDetail = null;
      this.threadMessages = [];

      try {
        const threadId = row.thread_id;
        const { data } = await axios.get(`/pablotify/thread/${threadId}`, {
          params: { loc: this.loc }
        });

        this.threadDetail = data.thread;
        this.threadMessages = data.messages || [];

      } catch (err) {
        console.error("openThread error:", err);
      } finally {
        this.loadingThread = false;
      }
    },

    closeThread() {
      this.showThreadModal = false;
      this.threadDetail = null;
      this.threadMessages = [];
    },

    async onInboxRowClick(row) {
      await this.handleInboxClick(row); // mark read if needed
      await this.openThread(row);
    },

    async closeThreadServer() {
      if (!this.threadDetail?.thread_id) return;

      try {
        await axios.put(`/pablotify/thread/${this.threadDetail.thread_id}/close`, {
          loc: this.loc
        });

        // ✅ Update local modal state
        this.threadDetail.is_closed = true;

        // ✅ Refresh inbox so it disappears from open-only view
        await this.loadInbox();

      } catch (err) {
        console.error("closeThreadServer error:", err);
      }
    },

    getResponseOptionsForRow(row) {
      if (!row) return [];

      // ✅ Production responding to Logistics “READY_BY_DATE”
      if (this.dept === "Production" && row.request_type === "READY_BY_DATE") {
        return this.productionResponseOptions;
      }

      // ✅ Logistics responding to Production “PROD_REQUEST_MOVE_OUT”
      if (this.dept === "Logistics" && row.request_type === "PROD_REQUEST_MOVE_OUT") {
        return this.logisticsResponseOptions;
      }

      return [];
    },








    prettyResponse(type, payload = {}) {
      switch (type) {
        case "YES_EXPECTED_DATE":
          return `Yes — expected ${this.formatDateOnly(payload.expected_date)}`;
        case "YES_IN_STOCK":
          return "Yes — will be in stock in time";
        case "NO_RM_SHORTAGE":
          return "No — RM Shortage";
        case "NO_MACHINE_DOWN":
          return "No — Machine Down";
        case "NO_OTHER":
          return "No — Other constraint";
        default:
          return type;
      }
    },



    async loadUnreadCount() {
      try {
        const { data } = await axios.get('/pablotify/unreadCount', {
          params: {
            dept: this.dept,
            loc: this.loc
          }
        });

        this.unreadCount = data.unresolved || 0;

      } catch (e) {
        // ignore
      }
    },

    async loadInbox() {
      this.loadingInbox = true;
      try {
        const { data } = await axios.get('/pablotify/inbox', {
          params: {
            dept: this.dept,
            loc: this.loc,
            include_closed: this.includeClosed
          }
        });

        this.inbox = data || [];

        // ✅ Vue 3: init draft response slots for Production responses
        const nextDrafts = { ...this.draftResponses };

        (this.inbox || []).forEach(row => {
          if (!nextDrafts[row.request_id]) {
            nextDrafts[row.request_id] = {
              response_type: "",
              expected_date: ""
            };
          }
        });

        this.draftResponses = nextDrafts;

      } catch (e) {
        this.inbox = [];
      } finally {
        this.loadingInbox = false;
        this.loadUnreadCount();
      }
    },

    renderPreview(type, payload = {}, isDraft = false) {
      if (!type) return isDraft ? "Select a request type to preview..." : "";

      const orders = (payload.order_nos || []).join(", ");
      const item = payload.item_no || "(item)";

      // ✅ format dates nicely (handles ISO strings)
      const req = payload.req_ship_dt
        ? this.formatDateOnly(payload.req_ship_dt)
        : "(req date)";

      const orig = payload.original_ship_dt
        ? this.formatDateOnly(payload.original_ship_dt)
        : "(original date)";

      const nw = payload.new_ship_dt
        ? this.formatDateOnly(payload.new_ship_dt)
        : "(new date)";

      switch (type) {
        case "READY_BY_DATE":
          return `Will ${item} be ready by ${req}? Order(s) ${orders || "(none selected)"}.`;

        case "MOVE_OUT_ORDERS":
          return `Move out order(s) ${orders || "(none selected)"} to ${nw} from ${orig}.`;

        case "ORDERS_MOVED_RERUN":
          return `Orders ${orders || "(none selected)"} moved to ${nw} from ${orig}. Please run planner again.`;
        
        case "PROD_REQUEST_MOVE_OUT": {
          const reason = payload.reason_code || "(reason)";
          const ship = payload.req_ship_dt ? this.formatDateOnly(payload.req_ship_dt) : "(req ship)";
          return `Production requests move out Order(s) ${orders || "(none)"} for ${item}. Req Ship: ${ship}. Reason: ${reason}.`;
        }




        default:
          return "";
      }
    },

    renderResponse(row) {
      if (!row?.response_type) return "";

      const rt = row.response_type;
      const rp = row.response_payload || {};
      const expected = rp.expected_date ? this.formatDateOnly(rp.expected_date) : null;

      switch (rt) {
        // ✅ Production responses
        case "YES_EXPECTED_DATE":
          return expected ? `Expected: ${expected}` : `Expected date provided`;
        case "NO_RM_SHORTAGE":
          return "No – RM shortage";
        case "NO_MACHINE_DOWN":
          return "No – machine down";
        case "NO_CAPACITY":
          return "No – capacity issue";

        // ✅ Logistics responses
        case "WILL_MOVE_OUT":
          return "Will move out";
        case "MOVED_OUT":
          return "Moved out";
        case "MOVED_AND_LOCKED":
          return "Moved + locked";
        case "CANNOT_MOVE_OUT":
          return "Cannot move out";

        default:
          return rt;
      }
    },



    async loadOrderOptions() {
      this.loadingOrders = true;
      try {
        const { data } = await axios.get('/pablotify/order-list', {
          params: {
            loc: this.loc
          }
        });

        this.orderOptions = data || [];

      } catch (e) {
        this.orderOptions = [];
      } finally {
        this.loadingOrders = false;
      }
    },

    async sendResponse(row) {
      const requestId = row.request_id;

      // ✅ ensure draft exists
      const draft = this.draftResponses?.[requestId];
      if (!draft || !draft.response_type) {
        this.responseErrors[requestId] = "Select a response first.";
        return;
      }

      // ✅ clear previous errors
      this.responseErrors[requestId] = "";
      this.responding[requestId] = true;

      try {
        const response_payload = {};

        // ✅ include expected date when required
        if (draft.response_type === "YES_EXPECTED_DATE") {
          if (!draft.expected_date) {
            this.responseErrors[requestId] = "Expected date is required for this response.";
            return;
          }
          response_payload.expected_date = draft.expected_date;
        }

        // ✅ send to backend
        const { data } = await axios.put(`/pablotify/respond/${requestId}`, {
          loc: this.loc,
          responded_by_dept: this.dept,
          response_type: draft.response_type,
          response_payload
        });

        // ✅ update the row in place (so UI instantly reflects response)
        row.response_type = data.response_type;
        row.response_payload = data.response_payload;
        row.responded_at = data.responded_at;
        row.responded_by_dept = data.responded_by_dept;

        // ✅ refresh unread count + inbox (optional but nice)
        this.loadUnreadCount();
        // await this.loadInbox(); // optional if you want it to re-pull latest

      } catch (err) {
        console.error("sendResponse error:", err);
        this.responseErrors[requestId] = "Failed to send response.";
      } finally {
        this.responding[requestId] = false;
      }
    },

    async submitMoveOut() {
      this.moveOutError = "";

      try {
        if (!this.moveOutForm.new_ship_dt) {
          this.moveOutError = "New Ship Date is required.";
          return;
        }

        if (!this.moveOutForm.order_nos || this.moveOutForm.order_nos.length === 0) {
          this.moveOutError = "No orders found to move.";
          return;
        }

        this.sendingMoveOut = true;

        const payload = {
          thread_id: this.moveOutRow?.thread_id || null,
          loc: this.loc,
          item_no: this.moveOutForm.item_no || null,
          sender_dept: this.dept,              // Logistics
          recipient_dept: "Production",
          request_type: "ORDERS_MOVED_RERUN",
          payload: {
            item_no: this.moveOutForm.item_no || null,
            order_nos: this.moveOutForm.order_nos || [],
            original_ship_dt: this.moveOutForm.original_ship_dt || null,
            new_ship_dt: this.moveOutForm.new_ship_dt || null,
            reason_code: this.moveOutForm.reason_code || null
          },
          requires_replan: false
        };

        await axios.post("/pablotify/request", payload);

        // ✅ close modal + refresh inbox
        this.showMoveOutModal = false;
        this.moveOutRow = null;

        // ✅ pull fresh inbox state
        await this.loadInbox();

      } catch (err) {
        console.error("submitMoveOut error:", err);
        this.moveOutError = "Failed to send move-out request. Check server logs.";
      } finally {
        this.sendingMoveOut = false;
      }
    },

    async handleInboxClick(row) {
      if (row.is_read === false && row.recipient_dept === this.dept) {
        try {
          await axios.put(`/pablotify/markRead/${row.request_id}`, {
            loc: this.loc
          });

          row.is_read = true;
          this.loadUnreadCount();

        } catch (e) {
          // ignore
        }
      }
    },






    prettyType(type) {
      const map = {
        READY_BY_DATE: "Will item be ready by date?",
        MOVE_OUT_ORDERS: "Move out order(s)",
        ORDERS_MOVED_RERUN: "Orders moved — rerun planner",
        PROD_REQUEST_MOVE_OUT: "Production requests move out"
      };
      return map[type] || type;
    },

    formatDateTime(dt) {
      if (!dt) return "";
      try {
        return new Date(dt).toLocaleString();
      } catch {
        return dt;
      }
    }
  }
};
</script>

<style scoped>
/* ============================================================
   PabloQuacious.vue - LOCAL STYLES (Option 2)
   Copy of essential Pablo modal/panel/button styles
   ============================================================ */

/* ✅ Match Pablo feel */
.page-heading {
  font-family: 'Cormorant Garamond', serif;
  font-size: 36px;
  color: #cbbfbf;
  margin-bottom: 20px;
  text-align: center;
}

.small-muted {
  font-size: 12px;
  opacity: 0.75;
  color: #cbbfbf;
}

.empty-unplanned {
  opacity: 0.6;
  font-size: 12px;
  padding: 10px;
  color: #cbbfbf;
}

/* ✅ Popup modal styling (matches JIMScheduling / MachineScheduling) */
.popup-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.popup-content {
  background-color: #0b0101;
  padding: 10px 20px;
  border-radius: 8px;
  text-align: left;
  max-width: 95vw;
  max-height: 90vh;
  overflow: auto;
  border: 1px solid #888;
  color: #cbbfbf;
  width: 900px; /* ✅ give it room */
}

/* ✅ Header + close button */
.popup-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.close-btn {
  background: transparent;
  border: none;
  color: #cbbfbf;
  font-size: 20px;
  cursor: pointer;
  font-weight: bold;
}

/* ✅ Title wrap */
.modal-title-wrap {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.modal-title-line {
  display: flex;
  align-items: center;
  gap: 8px;
}

.modal-subtitle {
  font-size: 12px;
  opacity: 0.8;
  color: #cbbfbf;
}

/* ✅ Panel styling (dark Pablo panels) */
.panel {
  background-color: #0b0101;
  border: 1px solid #888;
  border-radius: 10px;
  padding: 10px 12px;
  text-align: left;
}

/* ✅ Controls / header section */
.controls-wrap {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  gap: 14px;
  margin-bottom: 12px;
}

.control {
  display: flex;
  flex-direction: column;
  gap: 6px;
  text-align: left;
  width: 100%;
}

.control label {
  font-size: 13px;
  color: #cbbfbf;
  font-weight: 600;
}

.control select,
.control input {
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid #444;
  background-color: #111;
  color: #cbbfbf;
  min-width: 180px;
  font-family: inherit;
  width: 100%;
  box-sizing: border-box;
}

/* ✅ Buttons in Pablo style */
.btn {
  border-radius: 10px;
  padding: 8px 16px;
  width: 125px;
  background-color: #f3f4f6;
  border: 1px solid #cfd3d8;
  color: #333;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease;
  font-family: inherit;
}

.btn:hover:not(:disabled) {
  background-color: #e5e7eb;
  border-color: #bfc5cc;
}

.btn:disabled {
  background-color: #e5e7eb;
  color: #888;
  cursor: not-allowed;
}

.btn-small {
  width: auto;
  padding: 6px 12px;
  font-size: 13px;
}

/* ✅ Pills (same as Logistics) */
.pill {
  font-size: 12px;
  padding: 4px 9px;
  border-radius: 999px;
  background: #111827;
  border: 1px solid #333;
  color: #e5e7eb;
}

/* ✅ Tab buttons */
.tabs {
  display: flex;
  gap: 10px;
  margin-top: 10px;
}

.tabs .btn.active {
  background-color: #dcdfe4;
  border-color: #bfc5cc;
}

/* ✅ Inbox list */
.inbox-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 12px;
}

.inbox-item {
  border: 1px solid #333;
  border-radius: 10px;
  padding: 10px;
  background: #111827;
  cursor: pointer;
}

.inbox-item.unread {
  border-color: #888;
  background: #0f141c;
}

.inbox-top {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  font-weight: bold;
  color: #cbbfbf;
}

.inbox-mid {
  margin-top: 8px;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.inbox-body {
  margin-top: 10px;
  font-size: 13px;
  color: #e5e7eb;
}

/* ✅ Bell button */
.pablotify-wrapper {
  display: inline-block;
}

.pablotify-bell {
  border-radius: 10px;
  padding: 6px 12px;
  background-color: #f3f4f6;
  border: 1px solid #cfd3d8;
  color: #333;
  font-weight: 600;
  cursor: pointer;
  position: relative;
  transition: background-color 0.15s ease, border-color 0.15s ease;
  font-family: inherit;
}

.pablotify-bell:hover {
  background-color: #e5e7eb;
  border-color: #bfc5cc;
}

.pablotify-btn {
  position: relative;       /* required for badge positioning */
  width: 125px;             /* matches your button widths */
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

/* ✅ only appears when unreadCount > 0 */
.notif-badge {
  position: absolute;
  top: -6px;
  right: -6px;
  background: #c53030;
  color: white;
  font-size: 11px;
  border-radius: 999px;
  padding: 2px 7px;
  border: 1px solid #aa4444;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-weight: bold;
  white-space: nowrap;
}

.badge {
  position: absolute;
  top: -6px;
  right: -6px;
  background: #c53030;
  color: white;
  font-size: 12px;
  border-radius: 999px;
  padding: 2px 7px;
  min-width: 18px;
  text-align: center;
}

/* ✅ Danger pill (replan badge) */
.danger-pill {
  background: #2a0b0b !important;
  border-color: #aa4444 !important;
  color: #fca5a5 !important;
}

.response-box {
  margin-top: 10px;
  padding: 10px;
  border-radius: 10px;
  border: 1px solid #333;
  background: #0f141c;
  color: #e5e7eb;
  font-size: 13px;
}

.response-title {
  font-weight: 600;
}

.thread-list {
  margin-top: 10px;
  max-height: 70vh;
  overflow-y: auto;
  padding-right: 6px;
}



</style>
